import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  User as UserIcon,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronRight,
  Eye,
  Trash2,
  BookOpen,
  BarChart3,
  Users,
  Activity,
  TrendingUp,
} from 'lucide-react';
import { StatCard } from '@azyntor-labs/nexus-ui';
import { attemptsApi, examsApi, usersApi, proctoringApi, analyticsApi } from '@/api/endpoints';
import { ExamAttempt, Exam, User, Question, StudentAnswer, QuestionOption } from '@/types';
import { toast } from '@/utils/toast';
import { getErrorMessage } from '@/utils/errorHandler';
import { format } from 'date-fns';
import { ConfirmationModal } from '@azyntor-labs/nexus-ui';

interface GradingData {
  question_id: string;
  points_earned: number;
  feedback: string;
}

// Local violation interface matching API response
interface ViolationData {
  id: string;
  attempt_id: string;
  violation_type: string;
  type?: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  screenshot_url?: string | null;
  timestamp?: string;
  created_at?: string;
}

export const GradingPage = () => {
  const queryClient = useQueryClient();
  const { attemptId } = useParams<{ attemptId?: string }>();
  const navigate = useNavigate();

  const [selectedAttempt, setSelectedAttempt] = useState<ExamAttempt | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [gradingData, setGradingData] = useState<Record<string, GradingData>>({});
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'grading' | 'violations'>('grading');

  // Fetch overall tenant analytics
  const { data: tenantRes } = useQuery({
    queryKey: ['tenant-analytics-grading'],
    queryFn: () => analyticsApi.getTenantAnalytics(),
    staleTime: 60000,
  });

  const tenantStats = tenantRes?.data?.data as {
    total_exams?: number;
    total_attempts?: number;
    total_students?: number;
    active_exams?: number;
    average_pass_rate?: number;
  } | null;

  // Fetch pending grading submissions
  const { data: attemptsResponse, isLoading } = useQuery({
    queryKey: ['pending-grading'],
    queryFn: () => attemptsApi.listPendingGrading({ page: 1, per_page: 50 }),
  });

  const attempts = Array.isArray(attemptsResponse?.data?.data) ? attemptsResponse.data.data : [];

  // Fetch exams to get titles
  const { data: examsResponse } = useQuery({
    queryKey: ['exams-list'],
    queryFn: () => examsApi.list({ page: 1, per_page: 100 }),
  });

  // Handle exams response structure: data.data.data (nested pagination object)
  const exams = Array.isArray(examsResponse?.data?.data?.data)
    ? examsResponse.data.data.data
    : [];

  // Fetch users to get student names (fetch more to cover all students)
  const { data: usersResponse } = useQuery({
    queryKey: ['users-list-grading'],
    queryFn: () => usersApi.list({ page: 1, per_page: 500 }),
  });

  // Also fetch student-role users specifically in case the general list misses them
  const { data: studentUsersResponse } = useQuery({
    queryKey: ['student-users-list'],
    queryFn: () => usersApi.list({ page: 1, per_page: 500, role: 'student' }),
  });

  // Handle users response structure: data.data (direct array)
  const allUsers = Array.isArray(usersResponse?.data?.data) ? usersResponse.data.data : [];
  const studentUsers = Array.isArray(studentUsersResponse?.data?.data) ? studentUsersResponse.data.data : [];
  // Merge both lists, deduplicating by ID
  const userMap = new Map<string, User>();
  [...allUsers, ...studentUsers].forEach((u: User) => userMap.set(u.id, u));
  const users = Array.from(userMap.values());

  // Create maps for quick lookup
  const examTitles = exams.reduce((acc: Record<string, string>, exam: Exam) => {
    acc[exam.id] = exam.title;
    return acc;
  }, {} as Record<string, string>);

  const userDetails = users.reduce((acc: Record<string, User>, user: User) => {
    acc[user.id] = user;
    return acc;
  }, {} as Record<string, User>);

  // Fetch detailed attempt data when selected
  const { data: attemptResultResponse } = useQuery({
    queryKey: ['attempt-result', selectedAttempt?.id],
    queryFn: () => attemptsApi.getResult(selectedAttempt!.id),
    enabled: !!selectedAttempt,
  });

  const attemptResult = attemptResultResponse?.data.data;
  const attemptDetail = attemptResult?.attempt;
  const attemptAnswers = attemptResult?.answers || [];

  // Update selected attempt with answers from the detailed response
  if (selectedAttempt && attemptAnswers.length > 0 && !selectedAttempt.answers) {
    selectedAttempt.answers = attemptAnswers;
  }

  // Fetch exam questions when viewing attempt details
  const { data: examQuestionsResponse } = useQuery({
    queryKey: ['exam-questions', attemptDetail?.exam_id || selectedAttempt?.exam_id],
    queryFn: () => examsApi.getQuestions(attemptDetail?.exam_id || selectedAttempt!.exam_id),
    enabled: !!(attemptDetail?.exam_id || selectedAttempt?.exam_id),
  });

  const examQuestions = Array.isArray(examQuestionsResponse?.data?.data)
    ? examQuestionsResponse.data.data
    : [];

  // Create a map of question_id to question details
  const questionDetailsMap = examQuestions.reduce((acc: Record<string, Question>, question: Question) => {
    acc[question.id] = question;
    return acc;
  }, {} as Record<string, Question>);

  // Fetch violations data for the selected attempt
  const { data: violationsResponse, isLoading: isLoadingViolations } = useQuery({
    queryKey: ['attempt-violations', selectedAttempt?.id],
    queryFn: () => proctoringApi.listViolations({
      attempt_id: selectedAttempt!.id,
      limit: 100,
    }),
    enabled: !!selectedAttempt,
  });

  // Use proctoring API violations, fall back to attempt's embedded violations
  const apiViolations: ViolationData[] = violationsResponse?.data?.data?.violations || [];
  const attemptViolations: ViolationData[] = (selectedAttempt?.violations || []).map((v: any, idx: number) => ({
    id: v.id || `attempt-violation-${idx}`,
    attempt_id: selectedAttempt?.id || '',
    violation_type: v.violation_type || v.type || 'unknown',
    type: v.type,
    description: v.description || `${(v.violation_type || v.type || 'unknown').replace(/_/g, ' ')} violation detected`,
    severity: v.severity || 'medium',
    screenshot_url: v.screenshot_url || null,
    timestamp: v.timestamp,
    created_at: v.created_at,
  }));
  // Prefer proctoring API violations; if empty, use attempt-embedded violations
  const violations: ViolationData[] = apiViolations.length > 0 ? apiViolations : attemptViolations;
  const violationStats = {
    total: violations.length || selectedAttempt?.violation_count || 0,
    critical: violations.filter((v) => v.severity === 'critical').length,
    high: violations.filter((v) => v.severity === 'high').length,
    medium: violations.filter((v) => v.severity === 'medium').length,
    low: violations.filter((v) => v.severity === 'low').length,
    byType: violations.reduce((acc: Record<string, number>, v) => {
      const violationType = v.violation_type || v.type || 'unknown';
      acc[violationType] = (acc[violationType] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
  };

  // Handle URL parameter to select attempt
  useEffect(() => {
    if (attemptId && attempts.length > 0 && !selectedAttempt) {
      const attempt = attempts.find((a: ExamAttempt) => a.id === attemptId);
      if (attempt) {
        handleSelectAttempt(attempt);
      }
    }
  }, [attemptId, attempts]);

  // Grade mutation
  const gradeMutation = useMutation({
    mutationFn: (attemptId: string) => {
      // Calculate total score from grading data
      const totalScore = Object.values(gradingData).reduce(
        (sum, g) => sum + g.points_earned,
        0
      );

      // Get max score from exam's total_marks
      const examId = attemptDetail?.exam_id || selectedAttempt?.exam_id;
      const exam = exams.find((e: Exam) => e.id === examId);
      const maxScore = exam?.total_marks || 0;

      // Validate total score doesn't exceed max
      if (maxScore > 0 && totalScore > maxScore) {
        throw new Error(`Total score (${totalScore.toFixed(1)}) cannot exceed exam maximum (${maxScore})`);
      }

      return attemptsApi.grade(attemptId, {
        score: totalScore,
        feedback: 'Graded by instructor',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-grading'] });
      queryClient.invalidateQueries({ queryKey: ['attempt', selectedAttempt?.id] });
      toast.success('Grading submitted successfully!');
      setSelectedAttempt(null);
      setGradingData({});
      setCurrentQuestionIndex(0);
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to submit grading'));
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (attemptId: string) => attemptsApi.delete(attemptId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-grading'] });
      toast.success('Attempt deleted successfully');
      setDeleteConfirmId(null);
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to delete attempt'));
    },
  });

  // Handle delete
  const handleDelete = (attemptId: string) => {
    deleteMutation.mutate(attemptId);
  };

  // Handle selecting an attempt to grade
  const handleSelectAttempt = (attempt: ExamAttempt) => {
    setSelectedAttempt(attempt);

    // Find first question that needs manual grading
    let firstManualGradingIndex = 0;
    if (attempt.answers && Array.isArray(attempt.answers)) {
      firstManualGradingIndex = attempt.answers.findIndex(
        answer => answer.score === null || answer.score === undefined ||
        (answer.score === 0 && !answer.is_correct)
      );
      // If no manual grading needed, start at first question
      if (firstManualGradingIndex === -1) {
        firstManualGradingIndex = 0;
      }
    }
    setCurrentQuestionIndex(firstManualGradingIndex);

    // Initialize grading data with existing scores from auto-grading
    const initialGrading: Record<string, GradingData> = {};

    // Check if answers exist before iterating
    if (attempt.answers && Array.isArray(attempt.answers)) {
      attempt.answers.forEach((answer) => {
        // Pre-fill with auto-graded score if available
        const autoGradedScore = answer.score !== null && answer.score !== undefined ? answer.score : 0;

        initialGrading[answer.question_id] = {
          question_id: answer.question_id,
          points_earned: autoGradedScore,
          feedback: '',
        };
      });
    }

    setGradingData(initialGrading);

    // Navigate to URL with attempt ID
    navigate(`/teacher/grading/${attempt.id}`);
  };

  // Handle viewing attempt details without grading
  const handleViewDetails = (attempt: ExamAttempt) => {
    // For now, just start grading - could create a separate read-only view
    handleSelectAttempt(attempt);
  };

  // Handle grading input
  const handleGradingChange = (questionId: string, field: 'points' | 'feedback', value: number | string) => {
    // Validate points don't exceed question max
    if (field === 'points') {
      const answer = attemptDetail?.answers?.find((a: StudentAnswer) => a.question_id === questionId);
      const questionMaxScore = answer?.max_score || 10;
      const pointsValue = value as number;

      if (pointsValue > questionMaxScore) {
        toast.error(`Score cannot exceed ${questionMaxScore} points for this question`);
        return;
      }
    }

    setGradingData((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        question_id: questionId,
        points_earned: field === 'points' ? (value as number) : prev[questionId]?.points_earned || 0,
        feedback: field === 'feedback' ? (value as string) : prev[questionId]?.feedback || '',
      },
    }));
  };

  // Submit grading
  const handleSubmitGrading = () => {
    if (!selectedAttempt) return;

    // Validate only manual grading questions have been reviewed
    // Auto-graded questions (with is_correct set) are already complete
    const ungradedQuestions = (selectedAttempt?.answers || []).filter((a) => {
      // Skip auto-graded questions (they already have scores)
      if (a.is_correct !== null && a.is_correct !== undefined && a.score !== null) {
        return false; // This question is already auto-graded
      }
      // Check if manual grading is provided
      return !gradingData[a.question_id] || gradingData[a.question_id].points_earned === undefined;
    });

    if (ungradedQuestions.length > 0) {
      toast.error(`Please grade all manual questions before submitting (${ungradedQuestions.length} remaining)`);
      return;
    }

    gradeMutation.mutate(selectedAttempt.id);
  };

  // Calculate grading progress
  // Count auto-graded questions + manually graded questions
  const gradedCount = (selectedAttempt?.answers || []).filter((answer) => {
    // Auto-graded questions count as complete
    if (answer.is_correct !== null && answer.is_correct !== undefined && answer.score !== null) {
      return true;
    }
    // Manually graded questions
    return gradingData[answer.question_id]?.points_earned !== undefined &&
           gradingData[answer.question_id]?.points_earned !== null;
  }).length;

  const currentAnswer = selectedAttempt?.answers?.[currentQuestionIndex];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner" />
      </div>
    );
  }

  // Grading detail view
  if (selectedAttempt && attemptDetail) {
    return (
      <div className="min-h-screen pb-20">
        {/* Header */}
        <div className="glass-card rounded-none border-x-0 border-t-0 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => {
                    setSelectedAttempt(null);
                    setGradingData({});
                    setActiveTab('grading');
                    navigate('/teacher/grading');
                  }}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  ← Back to List
                </button>
                <div>
                  <h1 className="text-xl font-bold text-white">
                    {selectedAttempt?.exam_title || examTitles[attemptDetail.exam_id] || attemptDetail.exam_id}
                  </h1>
                  <p className="text-sm text-white/60">
                    Student: {(() => {
                      // Prefer backend-provided student_name from the selected attempt
                      if (selectedAttempt?.student_name) return selectedAttempt.student_name;
                      const sid = attemptDetail.student_id || attemptDetail.user_id;
                      const info = sid ? userDetails[sid] : null;
                      if (info) {
                        const name = `${info.first_name || ''} ${info.last_name || ''}`.trim();
                        return name || info.email;
                      }
                      return selectedAttempt?.student_email || sid || 'Unknown';
                    })()} • Submitted:{' '}
                    {attemptDetail.submit_time &&
                      format(new Date(attemptDetail.submit_time), 'PPp')}
                  </p>
                </div>
              </div>

              <button
                onClick={handleSubmitGrading}
                disabled={gradeMutation.isPending || gradedCount < (selectedAttempt?.answers?.length || 0)}
                className="px-6 py-2 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 rounded-lg hover:bg-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {gradeMutation.isPending ? 'Submitting...' : 'Submit Grading'}
              </button>
            </div>

            {/* Progress */}
            <div className="flex items-center justify-between text-sm text-white/60 mb-2">
              <div className="flex items-center space-x-4">
                <span>
                  Progress: {gradedCount} / {selectedAttempt?.answers?.length || 0} graded
                </span>
                <span className="text-cyan-400">
                  • {(selectedAttempt?.answers || []).filter(a => a.is_correct !== null && a.is_correct !== undefined && a.score !== null).length} auto-graded
                </span>
                <span className="text-amber-400">
                  • {(selectedAttempt?.answers || []).filter(a => !(a.is_correct !== null && a.is_correct !== undefined && a.score !== null)).length} manual
                </span>
              </div>
              <span>
                Total Score: {Object.values(gradingData).reduce((sum, g) => sum + g.points_earned, 0).toFixed(1)} /{' '}
                {(() => {
                  const examId = attemptDetail?.exam_id || selectedAttempt?.exam_id;
                  const exam = exams.find((e: Exam) => e.id === examId);
                  return exam?.total_marks || attemptDetail?.max_score || 0;
                })()}
              </span>
            </div>
            <div className="w-full bg-white/10 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-teal-500 to-cyan-500 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${(gradedCount / (selectedAttempt?.answers?.length || 1)) * 100}%`,
                }}
              />
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-white/[0.06] bg-[#0d0d12]/50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex space-x-4">
              <button
                onClick={() => setActiveTab('grading')}
                className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                  activeTab === 'grading'
                    ? 'border-teal-500 text-teal-400'
                    : 'border-transparent text-white/60 hover:text-white'
                }`}
              >
                Grading
                <span className="ml-2 px-2 py-0.5 rounded-full bg-white/10 text-xs">
                  {attemptDetail?.answers?.length || selectedAttempt?.answers?.length || 0} questions
                </span>
              </button>
              <button
                onClick={() => setActiveTab('violations')}
                className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                  activeTab === 'violations'
                    ? 'border-red-500 text-red-400'
                    : 'border-transparent text-white/60 hover:text-white'
                }`}
              >
                Violations
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  violations.length > 0 ? 'bg-red-500/20 text-red-400' : 'bg-white/10'
                }`}>
                  {violations.length}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Grading Tab Content */}
        {activeTab === 'grading' && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-4 gap-6">
            {/* Question navigation */}
            <div className="col-span-1">
              <div className="glass-card p-4 sticky top-24">
                <h3 className="font-semibold text-white mb-4">Questions</h3>
                <div className="space-y-2">
                  {(selectedAttempt?.answers || []).map((answer, index) => {
                    // Check if auto-graded
                    const isAutoGraded = answer.is_correct !== null && answer.is_correct !== undefined && answer.score !== null;
                    // Check if manually graded
                    const isManuallyGraded = gradingData[answer.question_id]?.points_earned !== undefined;
                    const isCurrent = index === currentQuestionIndex;

                    return (
                      <button
                        key={answer.question_id}
                        onClick={() => setCurrentQuestionIndex(index)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          isCurrent
                            ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white'
                            : isAutoGraded
                            ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/25'
                            : isManuallyGraded
                            ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25'
                            : 'bg-white/5 border border-white/[0.06] text-white/60 hover:bg-white/10'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>Q{index + 1}</span>
                          {isAutoGraded && <span className="text-xs">✓ Auto</span>}
                          {isManuallyGraded && !isAutoGraded && <CheckCircle className="w-4 h-4" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Grading content */}
            <div className="col-span-3">
              {currentAnswer && (
                <div className="space-y-6">
                  {/* Question card */}
                  <div className="glass-card p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <h2 className="text-lg font-semibold text-white">
                          Question {currentQuestionIndex + 1}
                        </h2>
                        {/* Auto-graded indicator */}
                        {currentAnswer.is_correct !== null && currentAnswer.is_correct !== undefined && currentAnswer.score !== null && (
                          <span className="px-2 py-1 bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 rounded text-xs font-medium flex items-center space-x-1">
                            <CheckCircle className="w-3 h-3" />
                            <span>Auto-Graded</span>
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-white/60">
                        Max Points: {questionDetailsMap[currentAnswer.question_id]?.points || currentAnswer.max_score || 0}
                      </span>
                    </div>

                    <div className="mb-6">
                      <div className="text-white leading-relaxed mb-4">
                        {questionDetailsMap[currentAnswer.question_id]?.question_text || `Question ID: ${currentAnswer.question_id}`}
                      </div>

                      {/* Question Type Badge */}
                      {questionDetailsMap[currentAnswer.question_id] && (
                        <div className="flex items-center space-x-2 text-sm mb-4">
                          <span className="px-2 py-1 bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 rounded text-xs font-medium">
                            {questionDetailsMap[currentAnswer.question_id].question_type?.toUpperCase() || 'UNKNOWN'}
                          </span>
                          <span className="px-2 py-1 bg-purple-500/15 text-purple-400 border border-purple-500/30 rounded text-xs font-medium">
                            {questionDetailsMap[currentAnswer.question_id].difficulty?.toUpperCase() || 'MEDIUM'}
                          </span>
                        </div>
                      )}

                      {/* Show Options for MCQ/Multiple Select */}
                      {questionDetailsMap[currentAnswer.question_id]?.options && (
                        <div className="space-y-3">
                          {/* Color Legend */}
                          <div className="flex items-center space-x-4 text-xs px-1">
                            <span className="text-white/50">Legend:</span>
                            <div className="flex items-center space-x-1">
                              <div className="w-3 h-3 rounded bg-emerald-500/30 border border-emerald-500/50"></div>
                              <span className="text-white/60">Correct Selection</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <div className="w-3 h-3 rounded bg-red-500/30 border border-red-500/50"></div>
                              <span className="text-white/60">Wrong Selection</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <div className="w-3 h-3 rounded bg-cyan-500/30 border border-cyan-500/50"></div>
                              <span className="text-white/60">Correct Answer (Not Selected)</span>
                            </div>
                          </div>

                          <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-4">
                            <h4 className="text-sm font-semibold text-white/80 mb-3">Answer Options:</h4>
                            <div className="space-y-3">
                              {questionDetailsMap[currentAnswer.question_id].options.map((option: QuestionOption, idx: number) => {
                                const correctAnswers = questionDetailsMap[currentAnswer.question_id].correct_answer || [];
                                const isCorrectAnswer = correctAnswers.includes(option.id) || correctAnswers.includes(option.text);
                                const isStudentAnswer = currentAnswer.answer?.includes(option.id) || currentAnswer.answer?.includes(option.text);
                                const isCorrectSelection = isCorrectAnswer && isStudentAnswer;
                                const isWrongSelection = !isCorrectAnswer && isStudentAnswer;

                                return (
                                  <div
                                    key={option.id || idx}
                                    className={`p-4 rounded-lg border-2 ${
                                      isCorrectSelection
                                        ? 'bg-emerald-500/10 border-emerald-500/50'
                                        : isWrongSelection
                                        ? 'bg-red-500/10 border-red-500/50'
                                        : isCorrectAnswer
                                        ? 'bg-cyan-500/10 border-cyan-500/50'
                                        : 'bg-white/[0.02] border-white/[0.06]'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                          <span className="font-mono text-sm text-white/60">
                                            {String.fromCharCode(65 + idx)}.
                                          </span>
                                          <span className="text-white">{option.text}</span>
                                        </div>
                                      </div>
                                      <div className="flex items-center space-x-2">
                                        {isCorrectAnswer && (
                                          <span className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 text-xs font-medium">
                                            ✓ Correct Answer
                                          </span>
                                        )}
                                        {isStudentAnswer && (
                                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                            isCorrectSelection
                                              ? 'bg-emerald-500/20 text-emerald-400'
                                              : 'bg-red-500/20 text-red-400'
                                          }`}>
                                            {isCorrectSelection ? '✓ Student (Correct)' : '✗ Student (Wrong)'}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Summary */}
                            <div className="mt-4 p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-white/60">Student selected:</span>
                                <span className="text-white font-medium">
                                  {currentAnswer.answer?.map((val: string) => {
                                    const opts = questionDetailsMap[currentAnswer.question_id].options;
                                    const optIdx = opts.findIndex((o: QuestionOption) => o.id === val || o.text === val);
                                    if (optIdx >= 0) return `${String.fromCharCode(65 + optIdx)}. ${opts[optIdx].text}`;
                                    return val; // fallback to raw value
                                  }).join(', ') || 'No answer'}
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-sm mt-2">
                                <span className="text-white/60">Correct answer(s):</span>
                                <span className="text-cyan-400 font-medium">
                                  {questionDetailsMap[currentAnswer.question_id]?.correct_answer?.map((val: string) => {
                                    const opts = questionDetailsMap[currentAnswer.question_id].options;
                                    const optIdx = opts.findIndex((o: QuestionOption) => o.id === val || o.text === val);
                                    if (optIdx >= 0) return `${String.fromCharCode(65 + optIdx)}. ${opts[optIdx].text}`;
                                    return val; // fallback to raw value (text-based correct answer)
                                  }).join(', ') || 'N/A'}
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-sm mt-2">
                                <span className="text-white/60">Result:</span>
                                <span className={`font-medium ${
                                  currentAnswer.is_correct ? 'text-emerald-400' : 'text-red-400'
                                }`}>
                                  {currentAnswer.is_correct ? '✓ Correct' : '✗ Incorrect'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Student's answer */}
                    <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-white/70 mb-2">
                        Student's Answer:
                      </h3>
                      {currentAnswer.code_answer ? (
                        <div className="bg-[#0d0d12] text-white p-4 rounded-lg font-mono text-sm overflow-x-auto border border-white/[0.06]">
                          <pre>{currentAnswer.code_answer}</pre>
                        </div>
                      ) : currentAnswer.answer && currentAnswer.answer.length > 0 ? (
                        <div className="space-y-1">
                          {currentAnswer.answer.map((answerVal: string, idx: number) => {
                            const option = questionDetailsMap[currentAnswer.question_id]?.options?.find(
                              (opt: QuestionOption) => opt.id === answerVal || opt.text === answerVal
                            );
                            return (
                              <div key={idx} className="text-white">
                                • {option?.text || answerVal}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-white/40 italic">No answer provided</p>
                      )}
                      {currentAnswer.is_correct !== null && currentAnswer.is_correct !== undefined && (
                        <div className="mt-3 flex items-center space-x-2">
                          {currentAnswer.is_correct ? (
                            <>
                              <CheckCircle className="w-5 h-5 text-emerald-400" />
                              <span className="text-sm font-medium text-emerald-400">
                                Automatically graded as correct • Score: {currentAnswer.score || 0}/{currentAnswer.max_score || 0}
                              </span>
                            </>
                          ) : (
                            <>
                              <XCircle className="w-5 h-5 text-red-400" />
                              <span className="text-sm font-medium text-red-400">
                                Automatically graded as incorrect • Score: {currentAnswer.score || 0}/{currentAnswer.max_score || 0}
                              </span>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Time spent */}
                    <div className="mt-4 flex items-center space-x-2 text-sm text-white/60">
                      <Clock className="w-4 h-4" />
                      <span>
                        Time spent: {Math.floor((currentAnswer?.time_spent || 0) / 60)} min{' '}
                        {(currentAnswer?.time_spent || 0) % 60} sec
                      </span>
                    </div>
                  </div>

                  {/* Grading input */}
                  <div className="glass-card p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Grading</h3>

                    {/* Auto-graded message */}
                    {currentAnswer.is_correct !== null && currentAnswer.is_correct !== undefined && currentAnswer.score !== null ? (
                      <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4 mb-4">
                        <div className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-cyan-400 mt-0.5" />
                          <div>
                            <h4 className="text-sm font-semibold text-cyan-400 mb-1">
                              This question was auto-graded
                            </h4>
                            <p className="text-sm text-cyan-400/80 mb-2">
                              Score: <span className="font-bold">{currentAnswer.score}/{currentAnswer.max_score}</span> points
                            </p>
                            <p className="text-xs text-cyan-400/60">
                              You can override this score if needed, or keep it and move to the next question.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
                        <div className="flex items-start space-x-3">
                          <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                          <div>
                            <h4 className="text-sm font-semibold text-amber-400 mb-1">
                              Manual grading required
                            </h4>
                            <p className="text-xs text-amber-400/70">
                              This question requires your evaluation. Please review the answer and assign points.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="space-y-4">
                      {/* Points */}
                      <div>
                        <label className="block text-sm font-medium text-white/70 mb-2">
                          Points Earned
                        </label>
                        <input
                          type="number"
                          min="0"
                          max={currentAnswer.max_score || 10}
                          step="0.5"
                          value={gradingData[currentAnswer.question_id]?.points_earned || currentAnswer.score || 0}
                          onChange={(e) =>
                            handleGradingChange(
                              currentAnswer.question_id,
                              'points',
                              parseFloat(e.target.value)
                            )
                          }
                          className="input-dark w-full"
                          placeholder="Enter points"
                        />
                        <p className="mt-1 text-xs text-white/40">
                          Max: {currentAnswer.max_score || 10} points
                        </p>
                      </div>

                      {/* Feedback */}
                      <div>
                        <label className="block text-sm font-medium text-white/70 mb-2">
                          Feedback (Optional)
                        </label>
                        <textarea
                          value={gradingData[currentAnswer.question_id]?.feedback || ''}
                          onChange={(e) =>
                            handleGradingChange(
                              currentAnswer.question_id,
                              'feedback',
                              e.target.value
                            )
                          }
                          rows={4}
                          className="input-dark w-full resize-none"
                          placeholder="Provide feedback to the student..."
                        />
                      </div>

                      {/* Quick actions */}
                      <div className="flex space-x-3">
                        <button
                          onClick={() => {
                            handleGradingChange(currentAnswer.question_id, 'points', currentAnswer.max_score || 10);
                          }}
                          className="px-4 py-2 bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 rounded-lg hover:bg-emerald-500/25 text-sm font-medium transition-colors"
                        >
                          Full Credit ({currentAnswer.max_score || 10})
                        </button>
                        <button
                          onClick={() => {
                            handleGradingChange(currentAnswer.question_id, 'points', (currentAnswer.max_score || 10) / 2);
                          }}
                          className="px-4 py-2 bg-amber-500/15 border border-amber-500/30 text-amber-400 rounded-lg hover:bg-amber-500/25 text-sm font-medium transition-colors"
                        >
                          Half Credit ({(currentAnswer.max_score || 10) / 2})
                        </button>
                        <button
                          onClick={() => {
                            handleGradingChange(currentAnswer.question_id, 'points', 0);
                          }}
                          className="px-4 py-2 bg-red-500/15 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/25 text-sm font-medium transition-colors"
                        >
                          No Credit
                        </button>
                      </div>
                    </div>

                    {/* Navigation */}
                    <div className="flex items-center justify-between mt-6 pt-6 border-t border-white/[0.06]">
                      <button
                        onClick={() => setCurrentQuestionIndex(currentQuestionIndex - 1)}
                        disabled={currentQuestionIndex === 0}
                        className="px-4 py-2 bg-white/5 border border-white/[0.06] rounded-lg text-white/70 hover:bg-white/10 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        ← Previous
                      </button>
                      <button
                        onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
                        disabled={currentQuestionIndex === (selectedAttempt?.answers?.length || 0) - 1}
                        className="px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg hover:shadow-glow-teal disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Violations Tab Content */}
        {activeTab === 'violations' && (
          <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
            {/* Violations Summary */}
            <div className="grid grid-cols-5 gap-4">
              <div className="glass-card p-4">
                <p className="text-sm text-white/60 mb-1">Total Violations</p>
                <p className="text-2xl font-bold text-white">{violationStats.total}</p>
              </div>
              <div className="glass-card p-4 border-l-2 border-red-500/50">
                <p className="text-sm text-red-400/80 mb-1">Critical</p>
                <p className="text-2xl font-bold text-red-400">{violationStats.critical}</p>
              </div>
              <div className="glass-card p-4 border-l-2 border-orange-500/50">
                <p className="text-sm text-orange-400/80 mb-1">High</p>
                <p className="text-2xl font-bold text-orange-400">{violationStats.high}</p>
              </div>
              <div className="glass-card p-4 border-l-2 border-amber-500/50">
                <p className="text-sm text-amber-400/80 mb-1">Medium</p>
                <p className="text-2xl font-bold text-amber-400">{violationStats.medium}</p>
              </div>
              <div className="glass-card p-4 border-l-2 border-cyan-500/50">
                <p className="text-sm text-cyan-400/80 mb-1">Low</p>
                <p className="text-2xl font-bold text-cyan-400">{violationStats.low}</p>
              </div>
            </div>

            {/* Violations by Type */}
            {Object.keys(violationStats.byType).length > 0 && (
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Violations by Type</h3>
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(violationStats.byType).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg">
                      <span className="text-sm text-white/70 capitalize">
                        {type.replace(/_/g, ' ')}
                      </span>
                      <span className="text-lg font-semibold text-white">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Violations Timeline */}
            {isLoadingViolations ? (
              <div className="glass-card p-8 text-center">
                <div className="spinner mx-auto mb-3" />
                <p className="text-white/60">Loading violations...</p>
              </div>
            ) : violations.length > 0 ? (
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Violation Timeline</h3>
                <div className="space-y-3">
                  {violations.map((violation, idx) => (
                    <div
                      key={violation.id || idx}
                      className={`p-4 border-l-4 rounded-lg ${
                        violation.severity === 'critical'
                          ? 'border-red-500 bg-red-500/10'
                          : violation.severity === 'high'
                          ? 'border-orange-500 bg-orange-500/10'
                          : violation.severity === 'medium'
                          ? 'border-amber-500 bg-amber-500/10'
                          : 'border-cyan-500 bg-cyan-500/10'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center space-x-2 mb-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              violation.severity === 'critical'
                                ? 'bg-red-500/20 text-red-400'
                                : violation.severity === 'high'
                                ? 'bg-orange-500/20 text-orange-400'
                                : violation.severity === 'medium'
                                ? 'bg-amber-500/20 text-amber-400'
                                : 'bg-cyan-500/20 text-cyan-400'
                            }`}>
                              {violation.severity?.toUpperCase()}
                            </span>
                            <span className="text-sm font-semibold text-white capitalize">
                              {(violation.violation_type || violation.type || '').replace(/_/g, ' ')}
                            </span>
                          </div>
                          <p className="text-sm text-white/70">{violation.description}</p>
                        </div>
                        <span className="text-xs text-white/50">
                          {violation.timestamp && format(new Date(violation.timestamp), 'HH:mm:ss')}
                          {violation.created_at && !violation.timestamp && format(new Date(violation.created_at), 'HH:mm:ss')}
                        </span>
                      </div>
                      {violation.screenshot_url && (
                        <div className="mt-3">
                          <img
                            src={violation.screenshot_url}
                            alt="Violation evidence"
                            className="rounded border border-white/10 max-h-48 object-contain"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="glass-card p-8 text-center">
                <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                <p className="text-white/60">No violations detected during this exam</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Pending Grading</h1>
        <p className="text-white/60">
          Review and grade exam submissions that require manual evaluation
        </p>
      </div>

      {/* Overall Stats */}
      {tenantStats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard
            title="Total Exams"
            value={tenantStats.total_exams ?? 0}
            icon={<BookOpen className="w-4 h-4" />}
            color="teal"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Total Attempts"
            value={tenantStats.total_attempts ?? 0}
            icon={<BarChart3 className="w-4 h-4" />}
            color="blue"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Total Students"
            value={tenantStats.total_students ?? 0}
            icon={<Users className="w-4 h-4" />}
            color="purple"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Active Exams"
            value={tenantStats.active_exams ?? 0}
            icon={<Activity className="w-4 h-4" />}
            color="green"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Avg Pass Rate"
            value={`${(tenantStats.average_pass_rate ?? 0).toFixed(1)}%`}
            icon={<TrendingUp className="w-4 h-4" />}
            color="orange"
            className="!p-3 [&_h3]:!text-xl"
          />
        </div>
      )}

      {attempts.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">All Caught Up!</h3>
          <p className="text-white/60">
            There are no exam submissions pending grading at this time.
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {attempts.map((attempt) => {
            // Use end_time as submitted time
            const submittedDate = attempt.end_time
              ? new Date(attempt.end_time)
              : null;
            const daysAgo = submittedDate
              ? Math.floor(
                  (Date.now() - submittedDate.getTime()) / (1000 * 60 * 60 * 24)
                )
              : null;

            // Use backend-provided student_name/exam_title from JOIN, fall back to lookup maps
            const examTitle = attempt.exam_title || examTitles[attempt.exam_id] || 'Unknown Exam';
            const studentInfo = (attempt.student_id && userDetails[attempt.student_id]) || (attempt.user_id && userDetails[attempt.user_id]);
            const studentName = attempt.student_name
              || (studentInfo ? `${studentInfo.first_name || ''} ${studentInfo.last_name || ''}`.trim() || studentInfo.email : null)
              || attempt.student_email
              || 'Unknown Student';

            return (
              <div
                key={attempt.id}
                className="glass-card p-6 hover:border-white/[0.12] transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-3">
                      <h3 className="text-lg font-semibold text-white">
                        {examTitle}
                      </h3>
                      <span className="px-2 py-1 bg-amber-500/15 text-amber-400 border border-amber-500/30 text-xs font-medium rounded">
                        Pending Grading
                      </span>
                      {daysAgo !== null && daysAgo > 0 && (
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded border ${
                            daysAgo > 3
                              ? 'bg-red-500/15 text-red-400 border-red-500/30'
                              : 'bg-orange-500/15 text-orange-400 border-orange-500/30'
                          }`}
                        >
                          {daysAgo} {daysAgo === 1 ? 'day' : 'days'} ago
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="flex items-center space-x-2 text-white/60">
                        <UserIcon className="w-4 h-4" />
                        <span>{studentName}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-white/60">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {submittedDate ? format(submittedDate, 'PPp') : 'N/A'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 text-white/60">
                        <Clock className="w-4 h-4" />
                        <span>
                          Time: {attempt.end_time && attempt.start_time
                            ? Math.floor((new Date(attempt.end_time).getTime() - new Date(attempt.start_time).getTime()) / (1000 * 60))
                            : 0} min
                        </span>
                      </div>
                    </div>

                    <div className="mt-3 flex items-center space-x-4 text-sm">
                      <span className="text-white/60">
                        Questions: {attempt.questions_answered || 0}
                      </span>
                      <span className="text-white/60">
                        Score: {attempt.score || 0}
                      </span>
                      <span className="text-white/60">
                        Result: {attempt.result || 'N/A'}
                      </span>
                      {((attempt?.violations && attempt.violations.length > 0) || (attempt?.violation_count && attempt.violation_count > 0)) && (
                        <div className="flex items-center space-x-1 text-red-400">
                          <AlertCircle className="w-4 h-4" />
                          <span>{attempt.violations?.length || attempt.violation_count || 0} violations</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleViewDetails(attempt)}
                      className="flex items-center space-x-2 px-4 py-2 bg-white/5 border border-white/[0.06] text-white/70 rounded-lg hover:bg-white/10 hover:text-white transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleSelectAttempt(attempt)}
                      className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg hover:shadow-glow-teal transition-all"
                    >
                      <span>Grade</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirmId(attempt.id)}
                      className="p-2 bg-red-500/15 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/25 transition-colors"
                      title="Delete Attempt"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={() => deleteConfirmId && handleDelete(deleteConfirmId)}
        title="Delete Attempt?"
        message="Are you sure you want to delete this exam attempt? This action cannot be undone and will permanently remove all associated data."
        variant="danger"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};
